import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import RBACService from 'service/RBAC/RBACService';
import { RoleListResp } from 'domain/payload/response/RBAC/RoleListResp';
import { hasRBACAccess } from '../../helpers/hasPrivilege';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

const RoleAccess: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [roles, setRoles] = useState<RoleListResp[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canManage = hasRBACAccess(profile, 'manage');

  const token = profile?.data?.token;
  const { getRoleList } = RBACService();

  const fetchRoles = async (search?: string) => {
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await getRoleList(token, currentPage, pageSize, search || null);
      setRoles(resp.data || []);
      setTotalPages(resp.pagination?.totalPage || 1);
      setTotalRecords(resp.pagination?.totalRecords || 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load roles';
      setError(errorMessage);
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [currentPage, pageSize]);

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    await fetchRoles(searchTerm);
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Role Access</h5>
            <div>
              <p>TransMore Role Access</p>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="p-4" style={{ background: 'white' }}>
            <div className="d-flex mb-3">
              <form className="d-flex" onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button type="submit" className="btn btn-primary px-4">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <div className="text-danger py-4 text-center">{error}</div>
            ) : (
              <table className="table table-condensed table-hover">
                <thead className="table-primary">
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Privilege Count</th>
                    <th>Created By</th>
                    <th>Created At</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {roles.length > 0 ? (
                    roles.map((role) => (
                      <tr key={role.id}>
                        <td>{role.code}</td>
                        <td>{role.name}</td>
                        <td>{role.priviledgeCount}</td>
                        <td>{role.createdBy}</td>
                        <td>{role.createdAt}</td>
                        <td>
                          <div className="d-flex">
                            {canManage && (
                              <>
                                <Link to={`/rbac/${role.id}`}>
                                  <button className="btn btn-outline-warning btn-small-compact border-0">
                                    <i className="fa-solid fa-edit"></i>
                                  </button>
                                </Link>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-3">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleAccess;
